import React, { useRef, useState } from 'react';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import { Switch } from 'formik-antd';
import FormItem from 'components/formik/FormItem';

import { useAppDispatch } from 'hooks/useAppDispatch';
import BaseAlert from 'components/wrappers/BaseAlert';
import { ApiErrorMessage } from 'constants/ErrorMessages';
import EnableConfirmTwoFAModal from 'components/modals/EnableConfirmTwoFAModal';
import DisableTwoFAModal from 'components/modals/DisableTwoFAModal';
import { useAppSelector } from 'hooks/useAppSelector';
import {
  isMultiAccountSelector,
  userSelector,
} from 'redux/selectors/authenticationSelectors';
import { sendCodeActivateTwoFA } from 'redux/reducers/accountSlice';
import { TwoFactorAuthSendMessageTypeEnum } from 'api/backofficeApiClient';

import styles from '../../AccountSettingsPage.module.less';

interface TwoFAFormValue {
  isTwoFactorAuthStatus?: boolean;
}
const validationSchema = Yup.object({
  isTwoFactorAuthStatus: Yup.boolean(),
});

export default function TwoFASection() {
  const [isEnable2FAModalOpen, setIsEnable2FAModalOpen] = useState(false);
  const [isDisable2FAModalOpen, setIsDisable2FAModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const user = useAppSelector(userSelector);
  const isMultiAccount = useAppSelector(isMultiAccountSelector);
  const dispatch = useAppDispatch();
  const formRef: any = useRef();

  const initialValues = {
    isTwoFactorAuthStatus: user?.isBOTwoFactorAuthStatus,
  };

  const handleViewDisable2FAModal = () => {
    setIsDisable2FAModalOpen(true);
  };

  const resetTwoFAStatus = (status: boolean) => {
    formRef.current.setFieldValue('isTwoFactorAuthStatus', status);
  };

  const handleCloseDisable2FAModal = () => {
    setIsDisable2FAModalOpen(false);
  };

  const handleViewEnable2FAModal = () => {
    setIsEnable2FAModalOpen(true);
  };

  const handleCloseEnable2FAModal = () => {
    setIsEnable2FAModalOpen(false);
  };

  const handleSubmit = async (data: TwoFAFormValue) => {
    if (!data.isTwoFactorAuthStatus) return handleViewDisable2FAModal();

    setIsLoading(true);

    const result = await dispatch(
      sendCodeActivateTwoFA(
        TwoFactorAuthSendMessageTypeEnum.EnableTwoFactorAuth,
      ),
    );

    if (sendCodeActivateTwoFA.fulfilled.match(result)) {
      handleViewEnable2FAModal();
    } else {
      resetTwoFAStatus(false);
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }

    setIsLoading(false);
  };

  return (
    <>
      {isDisable2FAModalOpen && (
        <DisableTwoFAModal
          onClose={handleCloseDisable2FAModal}
          onReset={resetTwoFAStatus}
        />
      )}
      {isEnable2FAModalOpen && (
        <EnableConfirmTwoFAModal
          type={TwoFactorAuthSendMessageTypeEnum.EnableTwoFactorAuth}
          onClose={handleCloseEnable2FAModal}
          onReset={resetTwoFAStatus}
        />
      )}
      <Formik
        enableReinitialize
        innerRef={formRef}
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
      >
        {({ submitForm }) => (
          <Form>
            <FormItem
              name="isTwoFactorAuthStatus"
              className={styles.formItemBox}
            >
              <div className={styles.switchBox}>
                <Switch
                  onClick={submitForm}
                  checkedChildren="On"
                  unCheckedChildren="Off"
                  name="isTwoFactorAuthStatus"
                  loading={isLoading}
                  disabled={isMultiAccount}
                />
              </div>
              <div className={styles.emailBox}>
                <div className={styles.subtitle}>
                  Enable Email Authentication - Two-Factor Security
                </div>
                <div className={styles.text}>
                  Use an extra layer of security via a unique code that will be
                  sent to your email as part of your Two-Factor Authentication
                  (2FA) when logging in or resetting your password.
                </div>
              </div>
            </FormItem>
          </Form>
        )}
      </Formik>
    </>
  );
}
